import { AfterContentChecked, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar as MatSnackBar } from "@angular/material/snack-bar";
import { Select, Store } from "@ngxs/store";
import { CompassAssessmentQuestionAutocomplete, CompassAutocomplete, CompassAutocompletes, CompassAutocompleteType, CompassScaleRange, CompassSummaryScaleAutocomplete, CompassTrackableScaleAutocomplete, CompassTrackableScaleType, DeleteCompassAutocomplete, SaveCompassAutocomplete, TrackableScaleRange } from "huddyapi";
import { Observable, timer } from "rxjs";
import { AppComponent } from "../../app.component";
import { CompassAnswerableGroup, CompassScaleRangeGroup, RangeID, TrackableScaleRangeGroup } from "../../builder/compass-form-objects";
import { DesktopErrorHandler } from "../../services/errorHandler.service";
import { LineRange, Range, RangeOverlapDependency, SavedGroupStatus, SavedObject, SavedSelectField, SavedTextField, SelectOptions, SimpleStatusField } from "../multisave";
import { Util } from "../util";

@Component({
  selector: 'compass-piece-dialog',
  templateUrl: 'compass-piece-dialog.component.html',
  styleUrls: ["./compass-piece-dialog.component.scss"]
})
export class CompassPieceDialog implements OnInit, AfterContentChecked {
  @Select(state => state.compassState.autocomplete) autocomplete$: Observable<CompassAutocompletes>;

  @ViewChild('fieldInfo', { static: false }) fieldInfo: ElementRef;
  @ViewChild('filter', { static: false }) set filterInput(filterInput: ElementRef) {
    this._filterInput = filterInput;
    this.focusFilter();
  }

  get filterInput(): ElementRef {
    return this._filterInput;
  }

  set editing(editing: boolean) {
    this._editing = editing;
    this.dialogRef.disableClose = this.editing;
  }

  get editing(): boolean {
    return this._editing;
  }

  set tab(tab: CompassAutocompleteType) {
    this._tab = tab;
    this.focusFilter();
    this.autoSelect();
  }

  get tab(): CompassAutocompleteType {
    return this._tab;
  }

  set filterText(filterText: string) {
    if(filterText == null)
      filterText = '';
    this._filterText = filterText;
    this.normalizedFilterText = this.normalizeName(filterText);
    this.applyFilter();
  }

  get filterText(): string {
    return this._filterText;
  }

  _working: boolean;
  _tab: CompassAutocompleteType;
  _filterInput: ElementRef;
  _filterText: string;
  normalizedFilterText: string;
  selected: SavedAutocomplete;
  _editing: boolean;
  autocompleteMap: Map<number, SavedAutocomplete>;
  savedAutocompletes: SavedAutocompletes;
  filteredAutocompletes: SavedAutocompletes;
  trackableScaleTypeOptions: SelectOptions<CompassTrackableScaleType>;
  indexToSelect: number;
  shouldScrollToNew: boolean;
  fieldInfoShowing: boolean;
  fieldInfoElem: HTMLElement;
  fieldInfoPosition: string;
  fieldInfoArrowPosition: string;

  constructor(
    public dialogRef: MatDialogRef<CompassPieceDialog>,
    public snackbar: MatSnackBar,
    public store: Store,
    public dialog: MatDialog,
    public errorHandler: DesktopErrorHandler,
    @Inject(MAT_DIALOG_DATA) public data: CompassPieceDialogData) {
    this.editing = false;
    this.shouldScrollToNew = false;
    this.indexToSelect = -1;
    this.fieldInfoShowing = false;
    this.trackableScaleTypeOptions = new SelectOptions<CompassTrackableScaleType>([
      {
        value: CompassTrackableScaleType.AVERAGE,
        displayValue: 'Average'
      },
      {
        value: CompassTrackableScaleType.COUNT,
        displayValue: 'Count'
      },
      {
        value: CompassTrackableScaleType.SUM,
        displayValue: 'Sum'
      }
    ]);
    this.savedAutocompletes = new SavedAutocompletes();
    this.autocompleteMap = new Map<number, SavedAutocomplete>();
    this._tab = data.type != null ? data.type : CompassAutocompleteType.TRACKABLE_SCALE;
    this.filterText = '';
    if(this.data.from != null && this.data.type != null)
      this.add(this.data.from, this.data.type);
  }

  ngOnInit() {
    this.focusFilter();
    this.autocomplete$.subscribe((autocompletes: CompassAutocompletes) => {
      let savedAutocompletes: SavedAutocompletes = new SavedAutocompletes(), autocompleteMap: Map<number, SavedAutocomplete> = new Map<number, SavedAutocomplete>();
      this.handleAutocompletes(autocompletes.trackableScales, savedAutocompletes, autocompleteMap, (autocomplete: CompassTrackableScaleAutocomplete): SavedTrackableScaleAutocomplete => new SavedTrackableScaleAutocomplete(autocomplete, this));
      this.handleAutocompletes(autocompletes.summaryScales, savedAutocompletes, autocompleteMap, (autocomplete: CompassSummaryScaleAutocomplete): SavedSummaryScaleAutocomplete => new SavedSummaryScaleAutocomplete(autocomplete, this));
      this.handleAutocompletes(autocompletes.questions, savedAutocompletes, autocompleteMap, (autocomplete: CompassAssessmentQuestionAutocomplete): SavedAssessmentQuestionAutocomplete => new SavedAssessmentQuestionAutocomplete(autocomplete, this));
      if(this.selected && !autocompleteMap.has(this.selected.id)) {
        if(this.editing && !this.selected.isSaving()) {
          this.selected.updateFrom(null);
          savedAutocompletes.add(this.selected);
          autocompleteMap.set(this.selected.id, this.selected);
        }
      }
      this.autocompleteMap = autocompleteMap;
      this.savedAutocompletes = savedAutocompletes;
      this.applyFilter();
    });
  }

  ngAfterContentChecked(): void {
    if(this.fieldInfoShowing && this.fieldInfoElem) {
      this.fieldInfoPosition = this.getFieldInfoPosition();
      this.fieldInfoArrowPosition = this.getFieldInfoArrowPosition();
    }
  }

  handleAutocompletes(autocompletes: CompassAutocomplete[], savedAutocompletes: SavedAutocompletes, autocompleteMap: Map<number, SavedAutocomplete>, construct: (autcomplete: CompassAutocomplete) => SavedAutocomplete) {
    autocompletes.forEach((autocomplete: CompassAutocomplete) => {
      if(autocomplete.createdFrom != null && this.autocompleteMap.has(autocomplete.createdFrom)) {
        let existing: SavedAutocomplete = this.autocompleteMap.get(autocomplete.createdFrom);
        existing.updateFrom(autocomplete);
        existing.reset();
        savedAutocompletes.add(existing);
        autocompleteMap.set(autocomplete.id, existing);
      } else if(this.autocompleteMap.has(autocomplete.id)) {
        let existing: SavedAutocomplete = this.autocompleteMap.get(autocomplete.id);
        existing.updateFrom(autocomplete);
        if(!(this.editing && this.isSelected(existing) && !existing.isSaving()))
          existing.reset(false);
        savedAutocompletes.add(existing);
        autocompleteMap.set(autocomplete.id, existing);
      } else {
        const savedAutocomplete: SavedAutocomplete = construct(autocomplete);
        savedAutocompletes.add(savedAutocomplete);
        autocompleteMap.set(autocomplete.id, savedAutocomplete);
      }
    });
  }

  scrollToNew(element: ElementRef) {
    if(!this.shouldScrollToNew)
      return;
    this.shouldScrollToNew = false;
    timer(0).subscribe(() => {
      element?.nativeElement?.scrollIntoViewIfNeeded();
    });
  }

  setShouldScrollToNew(): void {
    this.shouldScrollToNew = true;
  }

  translateRangeIndex(index: number): string {
    return RangeID.fromRangeIndex(index);
  }

  isLoading(): boolean {
    return AppComponent.loading;
  }

  isRestricted(): boolean {
    return this.data.type != null && this.data.from == null;
  }

  normalizeName(name: string): string {
    return (name == null ? '' : name).toLowerCase().replace(/\s/g, '');
  }

  getRelevantAutocompletes(): SavedAutocomplete[] {
    return this.filteredAutocompletes[SavedAutocompletes.getPropertyName(this.tab)];
  }

  applyFilter() {
    this.filteredAutocompletes = new SavedAutocompletes({
      trackableScales: this.normalizedFilterText == '' ? this.savedAutocompletes.trackableScales : this.savedAutocompletes.trackableScales.filter((autocomplete: SavedAutocomplete): boolean => this.normalizeName(autocomplete.getListingName()).includes(this.normalizedFilterText)),
      summaryScales: this.normalizedFilterText == '' ? this.savedAutocompletes.summaryScales : this.savedAutocompletes.summaryScales.filter((autocomplete: SavedAutocomplete): boolean => this.normalizeName(autocomplete.getListingName()).includes(this.normalizedFilterText)),
      questions: this.normalizedFilterText == '' ? this.savedAutocompletes.questions : this.savedAutocompletes.questions.filter((autocomplete: SavedAutocomplete): boolean => this.normalizeName(autocomplete.getListingName()).includes(this.normalizedFilterText))
    });
    this.autoSelect();
  }

  autoSelect() {
    if(this.selected && this.autocompleteMap.has(this.selected.id) && this.selected.type == this.tab && this.normalizeName(this.selected.getListingName()).includes(this.normalizedFilterText))
      return;
    let arr: SavedAutocomplete[] = this.getRelevantAutocompletes();
    if(arr.length == 0)
      this.select(null);
    else
      this.select(arr[Math.max(0, Math.min(arr.length - 1, this.indexToSelect))]);
  }

  select(autocomplete: SavedAutocomplete) {
    if(this.editing)
      return;
    if(autocomplete != this.selected) {
      this.indexToSelect = -1;
      this.fieldInfoElem = null;
      this.selected = autocomplete;
      this.focusFilter();
    }
  }

  isSelected(autocomplete: SavedAutocomplete): boolean {
    return this.selected?.id == autocomplete?.id;
  }

  add(from?: CompassAutocomplete, type?: CompassAutocompleteType) {
    let toAdd: SavedAutocomplete;
    from = from || null;
    switch(type != null ? type : this.tab) {
      case CompassAutocompleteType.TRACKABLE_SCALE:
        toAdd = new SavedTrackableScaleAutocomplete(from, this);
        break;
      case CompassAutocompleteType.SUMMARY_SCALE:
        toAdd = new SavedSummaryScaleAutocomplete(from, this);
        break;
      case CompassAutocompleteType.QUESTION:
        toAdd = new SavedAssessmentQuestionAutocomplete(from, this);
        break;
      default:
        return;
    }
    this.autocompleteMap.set(toAdd.id, toAdd);
    this.savedAutocompletes.add(toAdd);
    this.select(toAdd);
    this.edit();
  }

  edit(which: boolean = true) {
    if(which) {
      this.editing = true;
      return;
    }
    if(this.selected.isNew()) {
      this.selected.delete().then((remove: boolean) => {
        if(!remove)
          return;
        const toDelete: SavedAutocomplete = this.selected;
        this.savedAutocompletes.remove(toDelete.id, toDelete.type);
        this.autocompleteMap.delete(toDelete.id);
        this.editing = false;
        this.applyFilter();
        this.autoSelect();
      });
      return;
    }
    if(!this.selected.canReset() && !this.selected.isSaving()) {
      this.editing = false;
      return;
    }
    this.selected.reset(true).then((reset: boolean) => {
      if(reset)
        this.editing = false;
    });
  }

  save(autocomplete: SavedAutocomplete) {
    autocomplete.save().then((saved: boolean) => {
      if(saved) {
        this.editing = false;
        this.autoSelect();
      }
    });
  }

  delete(autocomplete: SavedAutocomplete) {
    this.indexToSelect = this.getSelectedIndex();
    autocomplete.delete().then((deleted: boolean) => {
      if(deleted) {
        this.editing = false;
        this.autoSelect();
      }
    });
  }

  choose(autocomplete: SavedAutocomplete) {
    this.dialogRef.close(autocomplete.getSaved());
  }

  focusFilter() {
    if(this.filterInput != null)
      this.filterInput.nativeElement.focus();
  }

  getTabText(): string {
    switch(this.tab) {
      case CompassAutocompleteType.TRACKABLE_SCALE:
        return 'trackable scale';
      case CompassAutocompleteType.SUMMARY_SCALE:
        return 'summary scale';
      case CompassAutocompleteType.QUESTION:
        return 'assessment question';
      default:
        return '?';
    }
  }

  getAddTitle(): string {
    return 'Add a new ' + this.getTabText();
  }

  getFilterPlaceholder(): string {
    return 'Filter ' + this.getTabText() + 's...';
  }

  getEmptyStateTitle(): string {
    const text: string = this.getTabText();
    if(this.savedAutocompletes[CompassAutocompletes.getPropertyName(this.tab)].length == 0)
      return 'No ' + text + 's';
    return 'No ' + text + 's matching your filter';
  }

  getEmptyStateDesc(): string {
    const text: string = this.getTabText();
    if(this.savedAutocompletes[CompassAutocompletes.getPropertyName(this.tab)].length == 0)
      return 'Create a new ' + text;
    return 'Change the filter text or create a new ' + text;
  }

  getSelectedIndex(): number {
    let result: number = -1;
    if(this.selected)
      result = this.getRelevantAutocompletes().findIndex((autocomplete: SavedAutocomplete): boolean => this.isSelected(autocomplete));
    return result;
  }

  selectNext(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    const arr: SavedAutocomplete[] = this.getRelevantAutocompletes();
    this.select(arr[(index + 1) % arr.length]);
  }

  selectPrevious(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    const arr: SavedAutocomplete[] = this.getRelevantAutocompletes();
    this.select(arr[index == 0 ? arr.length - 1 : index - 1]);
  }

  getFieldInfoPositionInfo(): { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } {
    if(!(this.fieldInfoElem && this.fieldInfo && this.fieldInfo.nativeElement))
      return null;
    let vMode: number = 0; //-1 = above, 0 = side, 1 = below
    const position = this.fieldInfoElem.getBoundingClientRect();
    if(position.y <= 17)
      vMode = 1;
    else if(position.bottom + 17 >= window.innerHeight)
      vMode = -1;
    const popoverSpace: number = this.fieldInfo.nativeElement.offsetWidth + 20;
    let leftSpace: boolean = position.left - popoverSpace > 0, rightSpace: boolean = position.right + popoverSpace < window.innerWidth;
    if(vMode == 0 && !(leftSpace || rightSpace))
      vMode = (position.top + position.height / 2 > window.innerHeight / 2) ? -1 : 1;
    return { position: position, vMode: vMode, leftSpace: leftSpace, rightSpace: rightSpace };
  }

  getFieldInfoArrowPosition(): string {
    let top: string = '0', left: string = '0', transform: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      switch(positionInfo.vMode) {
        case -1:
          top = (positionInfo.position.top - 11).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          transform = 'scaleY(-1)';
          break;
        case 0:
          top = (positionInfo.position.top + positionInfo.position.height / 2 - 4).toString() + 'px';
          if(positionInfo.rightSpace) {
            left = (positionInfo.position.right - 1).toString() + 'px';
            transform = 'rotate(-90deg)';
          } else {
            left = (positionInfo.position.left - 17).toString() + 'px';
            transform = 'rotate(90deg)';
          }
          break;
        case 1:
          top = (positionInfo.position.bottom + 4).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          break;
      }
    }
    return 'top:' + top + ';left:' + left + ';transform:' + transform + ';';
  }

  getFieldInfoPosition(): string {
    let top: string = '0', right: string = 'unset', bottom: string = 'unset', left: string = '0', maxHeight: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      if(positionInfo.vMode == 0) {
        maxHeight = (window.innerHeight - 20).toString() + 'px';
        const normalTop: number = positionInfo.position.top + positionInfo.position.height / 2 - 22;
        top = Math.min(Math.max(10, window.innerHeight - this.fieldInfo.nativeElement.offsetHeight - 10), normalTop).toString() + 'px';
        if(positionInfo.rightSpace) {
          left = (positionInfo.position.right + 11).toString() + 'px';
        } else {
          left = 'unset';
          right = (window.innerWidth - (positionInfo.position.left - 11)).toString() + 'px';
        }
      } else {
        const normalLeft: number = positionInfo.position.left + positionInfo.position.width / 2 - 22;
        const rightPos: number = positionInfo.position.right - positionInfo.position.width / 2 + 22;
        left = Math.min(Math.max(Math.max(10, rightPos - this.fieldInfo.nativeElement.offsetWidth), window.innerWidth - this.fieldInfo.nativeElement.offsetWidth - 10), normalLeft).toString() + 'px';
        if(positionInfo.vMode == -1) {
          top = 'unset';
          const bottomPos: number = positionInfo.position.top - 11;
          bottom = (window.innerHeight - bottomPos).toString() + 'px';
          maxHeight = (bottomPos - 10).toString() + 'px';
        } else {
          const topNum: number = positionInfo.position.bottom + 11;
          top = topNum.toString() + 'px';
          maxHeight = (window.innerHeight - topNum - 10).toString() + 'px';
        }
      }
    }
    return 'top:' + top + ';right:' + right + ';bottom:' + bottom + ';left:' + left + ';--max-height:' + maxHeight + ';';
  }

  showFieldInfo(event: any, text: string, example?: string) {
    if(!this.fieldInfo)
      return;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[0].innerText = text;
    const style: string = example?.length > 0 ? '' : 'display: none;';
    this.fieldInfo.nativeElement.getElementsByClassName('subheader')[0].style = style;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[1].style = style;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[1].innerText = example;
    this.fieldInfoElem = event.target;
    this.fieldInfoShowing = true;
  }

  hideFieldInfo() {
    this.fieldInfoShowing = false;
    this.fieldInfoElem = null;
  }

  ignoreKeyboardArrowEvent(event: any): boolean {
    return event.target.tagName == 'MAT-SELECT' || event.target.tagName == 'TEXTAREA';
  }
   
  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any): void {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 's':
        if((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey)) {
          event.preventDefault();
          if(this.editing && this.selected?.saveEnabled())
            this.save(this.selected);
        }
        break;
      case 'Enter':
        if(!this.ignoreKeyboardArrowEvent(event)) {
          event.preventDefault();
          if(!this.editing && this.selected?.type == this.data.type)
            this.choose(this.selected);
        }
        break;
      case 'ArrowUp':
        if(!this.ignoreKeyboardArrowEvent(event)) {
          event.preventDefault();
          this.selectPrevious();
        }
        break;
      case 'ArrowDown':
        if(!(this.ignoreKeyboardArrowEvent(event))) {
          event.preventDefault();
          this.selectNext();
        }
        break;
      default:
    }
  }
}

export interface CompassPieceDialogData {
  type?: CompassAutocompleteType;
  from?: CompassAutocomplete;
}

type SavedAutocomplete = SavedTrackableScaleAutocomplete | SavedAssessmentQuestionAutocomplete | SavedSummaryScaleAutocomplete;

export class SavedAutocompletes {
  trackableScales: SavedTrackableScaleAutocomplete[];
  summaryScales: SavedSummaryScaleAutocomplete[];
  questions: SavedAssessmentQuestionAutocomplete[];

  constructor(obj?: any) {
    this.trackableScales = obj?.trackableScales || [];
    this.summaryScales = obj?.summaryScales || [];
    this.questions = obj?.questions || [];
  }

  add(autocomplete: SavedAutocomplete): SavedAutocompletes {
    const propertyName: string = CompassAutocompletes.getPropertyName(autocomplete.type);
    if(propertyName?.length > 0) {
      let arr: SavedAutocomplete[] = (this[propertyName] as SavedAutocomplete[]);
      const index: number = arr.findIndex((compassAutocomplete: SavedAutocomplete) => compassAutocomplete.id == autocomplete.id);
      if(index >= 0)
        arr.splice(index, 1);
      arr.splice(Util.getSortedIndex(arr, autocomplete, (a: SavedAutocomplete, b: SavedAutocomplete): number => a.compareTo(b)), 0, autocomplete)
    }
    return this;
  }

  remove(id: number, type: CompassAutocompleteType): SavedAutocompletes {
    const propertyName: string = CompassAutocompletes.getPropertyName(type);
    if(propertyName?.length > 0) {
      let arr: SavedAutocomplete[] = this[propertyName] as SavedAutocomplete[];
      const index: number = arr.findIndex((compassAutocomplete: SavedAutocomplete) => compassAutocomplete.id == id);
      if(index >= 0)
        arr.splice(index, 1);
    }
    return this;
  }

  static getPropertyName(type: CompassAutocompleteType): string {
    switch(type) {
      case CompassAutocompleteType.TRACKABLE_SCALE:
        return 'trackableScales';
      case CompassAutocompleteType.SUMMARY_SCALE:
        return 'summaryScales';
      case CompassAutocompleteType.QUESTION:
        return 'questions';
      default:
        return null;
    }
  }
}

class SavedTrackableScaleAutocomplete extends SavedObject<CompassTrackableScaleAutocomplete> {
  objectName = 'trackable scale';
  type: CompassAutocompleteType;
  name: SavedTextField;
  scaleType: SavedSelectField<CompassTrackableScaleType>;
  scaleRangesGroup: SavedGroupStatus;
  scaleRanges: TrackableScaleRangeGroup[];
  savedScaleRanges: TrackableScaleRange[];
  rangesDependency: RangeOverlapDependency<Range>;
  rangeIdToIndexMap: Map<number, number>;
  statusField: SimpleStatusField;
  parent: CompassPieceDialog;

  constructor(from: CompassTrackableScaleAutocomplete, parent: CompassPieceDialog) {
    super(from, false, null, null, parent.dialog);
    this.type = CompassAutocompleteType.TRACKABLE_SCALE;
    this.parent = parent;
    this.rangesDependency = new RangeOverlapDependency();
    this.rangeIdToIndexMap = new Map<number, number>();
    this.scaleRangesGroup = new SavedGroupStatus(this.updators);
    this.scaleRanges = [];
    this.savedScaleRanges = this.getScaleRanges(from);
    this.statusField = new SimpleStatusField(this.scaleRangesGroup.updators, null,
      [() => !this.scaleType || this.scaleType.value == null || this.scaleRanges?.length > 0 ? '' : 'Must provide at least one scale range'], (): boolean =>
      this.scaleRanges.length != this.savedScaleRanges.length || this.scaleRanges.findIndex((scaleRange: TrackableScaleRangeGroup): boolean => scaleRange.isNew) != -1);
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, null, null, null, null, true, null, 50);
    this.scaleType = new SavedSelectField<CompassTrackableScaleType>(this.getScaleType(from), null, this.updators, null,
      null, false, null, null, new SelectOptions<CompassTrackableScaleType>([
        { value: CompassTrackableScaleType.AVERAGE, displayValue: 'Average' },
        { value: CompassTrackableScaleType.COUNT, displayValue: 'Count' },
        { value: CompassTrackableScaleType.SUM, displayValue: 'Sum' }
      ]), null, (oldValue: CompassTrackableScaleType, newValue: CompassTrackableScaleType, field: SavedSelectField<CompassTrackableScaleType>): void => {
        if(newValue == oldValue)
          return;
        if(newValue == null || oldValue == null || newValue == CompassTrackableScaleType.COUNT || oldValue == CompassTrackableScaleType.COUNT) {
          this.clearScaleRanges();
          if(newValue == field.savedValue)
            this.setScaleRanges(this.savedScaleRanges, field);
        }
      }
    );
    this.setScaleRanges(this.savedScaleRanges);
  }

  getSavedName(): string {
    return this.name.savedValue;
  }

  beforeGroupChanged() {}

  onGroupChanged() {}

  private clearScaleRanges(shouldUpdate: boolean = true) {
    this.scaleRanges.forEach((range: TrackableScaleRangeGroup) => range.destroy());
    this.scaleRanges = [];
    this.rangeIdToIndexMap.clear();
    if(shouldUpdate)
      this.statusField.update();
  }

  setScaleRanges(scaleRanges: TrackableScaleRange[], scaleTypeField: SavedSelectField<CompassTrackableScaleType> = this.scaleType) {
    if(scaleRanges?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scaleRanges.length, this.scaleRanges.length);
      while(i < minLength) {
        this.scaleRanges[i].updateFrom(scaleRanges[i]);
        i++;
      }
      while(i < scaleRanges.length) {
        this.addScaleRange(scaleRanges[i], scaleTypeField), false;
        i++;
      }
      while(i < this.scaleRanges.length)
        this.removeScaleRange(this.scaleRanges[i].id, false);
    } else
      this.clearScaleRanges(false);
    this.statusField.update();
  }

  onUpdateFrom(from: CompassTrackableScaleAutocomplete) {
    this.name.savedValue = from ? from.name : null;
    this.scaleType.savedValue = this.getScaleType(from);
    this.savedScaleRanges = this.getScaleRanges(from);
    this.scaleRanges.forEach((scaleRange: TrackableScaleRangeGroup, index: number) => scaleRange.updateFrom(this.savedScaleRanges[index]));
    this.statusField.update();
  }

  touchAll(value: boolean): void {
    this.statusField.touch(value);
    this.name.touch(value);
    this.scaleType.touch(value);
    this.scaleRanges.forEach((rangeGroup: TrackableScaleRangeGroup) => rangeGroup.touchAll(value));
  }

  warnValidation(): void {
    this.parent.snackbar.open('Fix validation issues', 'Dismiss', {
      duration: 3000
    });
  }

  onReset(): void {
    this.name.reset();
    this.scaleType.reset();
    this.setScaleRanges(this.savedScaleRanges);
    this.scaleRanges.forEach((rangeGroup: TrackableScaleRangeGroup) => rangeGroup.resetAll());
    this.statusField.reset();
    this.statusField.update();
  }

  getSaved(): CompassTrackableScaleAutocomplete {
    return new CompassTrackableScaleAutocomplete({
      id: this.id,
      name: this.name.value,
      type: this.type,
      value: this.scaleRanges.map((rangeGroup: TrackableScaleRangeGroup): TrackableScaleRange => rangeGroup.getSaved(this.scaleType.value))
    });
  }

  async onSave(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new SaveCompassAutocomplete(this.getSaved())).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue)
      return false;
    });
  }

  async onDeleteExisting(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new DeleteCompassAutocomplete(this.id)).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue);
      return false;
    });
  }

  onDeleteNew(): void {}

  onDetach(): void {}

  getScaleRanges(from?: CompassTrackableScaleAutocomplete): TrackableScaleRange[] {
    return from?.value || [];
  }

  getScaleType(from?: CompassTrackableScaleAutocomplete): CompassTrackableScaleType {
    const scaleRanges: TrackableScaleRange[] = this.getScaleRanges(from);
    return scaleRanges.length > 0 ? scaleRanges[0].type : CompassTrackableScaleType.AVERAGE;
  }

  addScaleRange(from?: TrackableScaleRange, scaleTypeField: SavedSelectField<CompassTrackableScaleType> = this.scaleType, shouldUpdate: boolean = true) {
    const range: TrackableScaleRangeGroup = new TrackableScaleRangeGroup(from, scaleTypeField.value, this.scaleRangesGroup.updators, this.rangesDependency, this.rangeIdToIndexMap);
    this.rangeIdToIndexMap.set(range.id, this.scaleRanges.length);
    this.scaleRanges.push(range);
    if(shouldUpdate)
      this.statusField.update();
  }

  removeScaleRange(id: number, shouldUpdate: boolean = true) {
    const index: number = this.rangeIdToIndexMap.get(id);
    if(index == null)
      return;
    this.scaleRanges[index].destroy();
    this.scaleRanges.splice(index, 1);
    for(let i = index; i < this.scaleRanges.length; i++)
      this.rangeIdToIndexMap.set(this.scaleRanges[i].id, i);
    this.rangeIdToIndexMap.delete(id);
    if(shouldUpdate)
      this.statusField.update();
  }

  compareTo(other: SavedAutocomplete): number {
    return this.getSaved().compareTo((other as SavedTrackableScaleAutocomplete).getSaved());
  }

  static compare(a: SavedTrackableScaleAutocomplete, b: SavedTrackableScaleAutocomplete): number {
    return a.compareTo(b);
  }
}

class SavedAssessmentQuestionAutocomplete extends SavedObject<CompassAssessmentQuestionAutocomplete> {
  objectName = 'assessment question';
  type: CompassAutocompleteType;
  name: SavedTextField;
  questionGroup: CompassAnswerableGroup;
  parent: CompassPieceDialog;

  constructor(from: CompassAssessmentQuestionAutocomplete, parent: CompassPieceDialog) {
    super(from, false, null, null, parent.dialog);
    this.type = CompassAutocompleteType.QUESTION;
    this.parent = parent;
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, null, null, null, null, true, null, 50);
    this.questionGroup = new CompassAnswerableGroup(from?.value, this.updators, null, false, false, false);
  }

  getSavedName(): string {
    return this.name.savedValue;
  }

  beforeGroupChanged() {}

  onGroupChanged() {}

  onUpdateFrom(from: CompassAssessmentQuestionAutocomplete) {
    this.name.savedValue = from ? from.name : null;
    this.questionGroup.updateFrom(from?.value);
  }

  touchAll(value: boolean): void {
    this.name.touch(value);
    this.questionGroup.touchAll(value);
  }

  warnValidation(): void {
    this.parent.snackbar.open('Fix validation issues', 'Dismiss', {
      duration: 3000
    });
  }

  onReset(): void {
    this.name.reset();
    this.questionGroup.resetAll();
  }

  getSaved(): CompassAssessmentQuestionAutocomplete {
    return new CompassAssessmentQuestionAutocomplete({
      id: this.id,
      name: this.name.value,
      type: this.type,
      value: this.questionGroup.getSaved()
    });
  }

  async onSave(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new SaveCompassAutocomplete(this.getSaved())).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue)
      return false;
    });
  }

  async onDeleteExisting(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new DeleteCompassAutocomplete(this.id)).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue);
      return false;
    });
  }

  onDeleteNew(): void { }

  onDetach(): void { }

  compareTo(other: SavedAutocomplete): number {
    return this.getSaved().compareTo((other as SavedAssessmentQuestionAutocomplete).getSaved());
  }

  static compare(a: CompassAssessmentQuestionAutocomplete, b: CompassAssessmentQuestionAutocomplete): number {
    return a.compareTo(b);
  }
}

class SavedSummaryScaleAutocomplete extends SavedObject<CompassSummaryScaleAutocomplete> {
  objectName = 'summary scale';
  type: CompassAutocompleteType;
  name: SavedTextField;
  scaleRangesGroup: SavedGroupStatus;
  scaleRanges: CompassScaleRangeGroup[];
  savedScaleRanges: CompassScaleRange[];
  rangesDependency: RangeOverlapDependency<LineRange>;
  rangeIdToIndexMap: Map<number, number>;
  statusField: SimpleStatusField;
  parent: CompassPieceDialog;

  constructor(from: CompassSummaryScaleAutocomplete, parent: CompassPieceDialog) {
    super(from, false, null, null, parent.dialog);
    this.type = CompassAutocompleteType.SUMMARY_SCALE;
    this.parent = parent;
    this.rangesDependency = new RangeOverlapDependency();
    this.rangeIdToIndexMap = new Map<number, number>();
    this.scaleRangesGroup = new SavedGroupStatus(this.updators);
    this.scaleRanges = [];
    this.savedScaleRanges = this.getScaleRanges(from);
    this.statusField = new SimpleStatusField(this.scaleRangesGroup.updators, null,
      [() => this.scaleRanges?.length > 0 ? '' : 'Must provide at least one scale range'], (): boolean =>
      this.scaleRanges.length != this.savedScaleRanges.length || this.scaleRanges.findIndex((scaleRange: CompassScaleRangeGroup): boolean => scaleRange.isNew) != -1);
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, null, null, null, null, true, null, 50);
    this.setScaleRanges(this.savedScaleRanges);
  }

  getSavedName(): string {
    return this.name.savedValue;
  }

  beforeGroupChanged() {}

  onGroupChanged() {}

  private clearScaleRanges(shouldUpdate: boolean = true) {
    this.scaleRanges.forEach((range: CompassScaleRangeGroup) => range.destroy());
    this.scaleRanges = [];
    this.rangeIdToIndexMap.clear();
    if(shouldUpdate)
      this.statusField.update();
  }

  setScaleRanges(scaleRanges: CompassScaleRange[]) {
    if(scaleRanges?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scaleRanges.length, this.scaleRanges.length);
      while(i < minLength) {
        this.scaleRanges[i].updateFrom(scaleRanges[i]);
        i++;
      }
      while(i < scaleRanges.length) {
        this.addScaleRange(scaleRanges[i]), false;
        i++;
      }
      while(i < this.scaleRanges.length)
        this.removeScaleRange(this.scaleRanges[i].id, false);
    } else
      this.clearScaleRanges(false);
    this.statusField.update();
  }

  onUpdateFrom(from: CompassSummaryScaleAutocomplete) {
    this.name.savedValue = from ? from.name : null;
    this.savedScaleRanges = this.getScaleRanges(from);
    this.scaleRanges.forEach((scaleRange: CompassScaleRangeGroup, index: number) => scaleRange.updateFrom(this.savedScaleRanges[index]));
    this.statusField.update();
  }

  touchAll(value: boolean): void {
    this.statusField.touch(value);
    this.name.touch(value);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => rangeGroup.touchAll(value));
  }

  warnValidation(): void {
    this.parent.snackbar.open('Fix validation issues', 'Dismiss', {
      duration: 3000
    });
  }

  onReset(): void {
    this.name.reset();
    this.setScaleRanges(this.savedScaleRanges);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => rangeGroup.resetAll());
    this.statusField.reset();
    this.statusField.update();
  }

  getSaved(): CompassSummaryScaleAutocomplete {
    return new CompassSummaryScaleAutocomplete({
      id: this.id,
      name: this.name.value,
      type: this.type,
      value: this.scaleRanges.map((rangeGroup: CompassScaleRangeGroup): CompassScaleRange => rangeGroup.getSaved())
    });
  }

  async onSave(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new SaveCompassAutocomplete(this.getSaved())).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue)
      return false;
    });
  }

  async onDeleteExisting(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new DeleteCompassAutocomplete(this.id)).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.name.savedValue);
      return false;
    });
  }

  onDeleteNew(): void { }

  onDetach(): void { }

  getScaleRanges(from?: CompassSummaryScaleAutocomplete): CompassScaleRange[] {
    return from?.value || [];
  }

  addScaleRange(from?: CompassScaleRange, shouldUpdate: boolean = true) {
    const range: CompassScaleRangeGroup = new CompassScaleRangeGroup(from, this.scaleRangesGroup.updators, this.rangesDependency, this.rangeIdToIndexMap);
    this.rangeIdToIndexMap.set(range.id, this.scaleRanges.length);
    this.scaleRanges.push(range);
    if(shouldUpdate)
      this.statusField.update();
  }

  removeScaleRange(id: number, shouldUpdate: boolean = true) {
    const index: number = this.rangeIdToIndexMap.get(id);
    if(index == null)
      return;
    this.scaleRanges[index].destroy();
    this.scaleRanges.splice(index, 1);
    for(let i = index; i < this.scaleRanges.length; i++)
      this.rangeIdToIndexMap.set(this.scaleRanges[i].id, i);
    this.rangeIdToIndexMap.delete(id);
    if(shouldUpdate)
      this.statusField.update();
  }

  compareTo(other: SavedAutocomplete): number {
    return this.getSaved().compareTo((other as SavedSummaryScaleAutocomplete).getSaved());
  }

  static compare(a: SavedSummaryScaleAutocomplete, b: SavedSummaryScaleAutocomplete): number {
    return a.compareTo(b);
  }
}